import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray } from '@angular/forms';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';

@Component({
    selector: 'app-inspection-maintenance',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './inspection-maintenance.component.html',
    styleUrl: './inspection-maintenance.component.scss'
})
export class InspectionMaintenanceComponent implements OnInit {
    @Input() inspectionData: any;
    @Input() confirmAction: any;
    @Input() userEmail!: string;
    @Input() userId!: number;
    @Input() userName!: string;

    maintenanceForm!: FormGroup;
    isSubmitting: boolean = false;
    isLoadingVehicle: boolean = false;
    isLoadingProviders: boolean = false;
    isLoadingConcepts: boolean = false;

    vehicleData: any = null;
    providers: any[] = [];
    concepts: any[] = [];

    constructor(
        private fb: FormBuilder,
        public modal: NgbModal,
        private http: HttpClient
    ) {}

    ngOnInit(): void {
        console.log('🔍 InspectionData recibida:', this.inspectionData);

        this.initForm();
        this.loadProviders();
        this.loadConcepts();

        if (this.inspectionData) {
            this.loadVehicleDataFromInspection();
        }
    }

    initForm(): void {
        this.maintenanceForm = this.fb.group({
            vehicleId: [null, Validators.required],
            providerId: [null, Validators.required],
            maintenanceType: ['correctivo', Validators.required],
            scheduledDate: [null],
            requestedByName: [this.userName || '', Validators.required],
            requestedByUser: [this.userId, Validators.required],
            serviceId: [null],
            inspectionId: [null],
            km: ['', Validators.required],
            operatorDiscountType: ['ninguno'],
            operatorDiscountValue: [0],
            items: this.fb.array([])
        });

        // Agregar primera partida
        this.addItem();
    }

    get itemsArray(): FormArray {
        return this.maintenanceForm.get('items') as FormArray;
    }

    addItem(): void {
        const itemGroup = this.fb.group({
            conceptId: [null, Validators.required],
            amount: [0, [Validators.required, Validators.min(0.01)]]
        });
        this.itemsArray.push(itemGroup);
    }

    removeItem(index: number): void {
        if (this.itemsArray.length > 1) {
            this.itemsArray.removeAt(index);
        }
    }

    loadVehicleDataFromInspection(): void {
        console.log('📋 Cargando datos desde inspección...');

        // Obtener el ID del vehículo desde v.id
        const vehicleId = this.inspectionData['v.id'];

        console.log('🚗 Vehicle ID:', vehicleId);

        if (!vehicleId) {
            console.error('❌ No se encontró v.id en inspectionData');
            console.log('Campos disponibles:', Object.keys(this.inspectionData));
            return;
        }

        // Pre-llenar datos de inspección
        const inspectionId = this.inspectionData['vi.id'];
        const serviceId = this.inspectionData['s.id'];
        const km = this.inspectionData['vi.km'];

        console.log('📝 Pre-llenando datos:', { inspectionId, serviceId, km });

        this.maintenanceForm.patchValue({
            inspectionId: inspectionId,
            serviceId: serviceId,
            km: km
        });

        // Cargar datos completos del vehículo
        this.loadVehicleData(vehicleId);
    }

    loadVehicleData(vehicleId: number): void {
        console.log('🔄 Llamando al endpoint get-vehicle-data con ID:', vehicleId);

        this.isLoadingVehicle = true;
        const headers = new HttpHeaders().set('X-Api-Key', environment.API_KEY);

        this.http.get(
            `${environment.SERVICES_URL}/maintenance/get-vehicle-data?vehicleId=${vehicleId}`,
            { headers }
        ).subscribe({
            next: (response: any) => {
                console.log('✅ Respuesta del servidor:', response);

                this.isLoadingVehicle = false;
                if (response.success) {
                    this.vehicleData = response.data;
                    console.log('🚗 Datos del vehículo cargados:', this.vehicleData);

                    // Actualizar vehicleId en el formulario
                    this.maintenanceForm.patchValue({
                        vehicleId: response.data.vehicleId
                    });

                    // Si no hay KM en el form, usar el del vehículo
                    if (!this.maintenanceForm.get('km')?.value) {
                        this.maintenanceForm.patchValue({
                            km: response.data.km
                        });
                    }
                } else {
                    console.error('❌ Error en respuesta:', response.message);
                    alert('Error al cargar datos del vehículo: ' + response.message);
                }
            },
            error: (error) => {
                console.error('❌ Error HTTP:', error);
                this.isLoadingVehicle = false;
                alert('Error al cargar datos del vehículo. Verifique la consola.');
            }
        });
    }

    loadProviders(): void {
        this.isLoadingProviders = true;
        const headers = new HttpHeaders().set('X-Api-Key', environment.API_KEY);

        this.http.post(
            `${environment.SERVICES_URL}/table/loadselect`,
            { table: 'maintenanceProviders' },
            { headers }
        ).subscribe({
            next: (response: any) => {
                this.isLoadingProviders = false;
                if (!response.error) {
                    this.providers = response.message;
                    console.log('👷 Proveedores cargados:', this.providers.length);
                }
            },
            error: (error) => {
                console.error('❌ Error cargando proveedores:', error);
                this.isLoadingProviders = false;
            }
        });
    }

    loadConcepts(): void {
        this.isLoadingConcepts = true;
        const headers = new HttpHeaders().set('X-Api-Key', environment.API_KEY);

        this.http.post(
            `${environment.SERVICES_URL}/table/loadselect`,
            {
                table: 'confList',
                idColumn: 'idconfigurableLists',
                valueColumn: 'configurableListsCode',
                conditionColumn: 'configurableListsField',
                conditionValue: 'MaintenanceConcept'
            },
            { headers }
        ).subscribe({
            next: (response: any) => {
                this.isLoadingConcepts = false;
                if (!response.error) {
                    this.concepts = response.message;
                    console.log('🔧 Conceptos cargados:', this.concepts.length);
                }
            },
            error: (error) => {
                console.error('❌ Error cargando conceptos:', error);
                this.isLoadingConcepts = false;
            }
        });
    }

    calculateSubtotal(): number {
        return this.itemsArray.controls.reduce((sum, control) => {
            return sum + (parseFloat(control.get('amount')?.value) || 0);
        }, 0);
    }

    calculateIVA(): number {
        return this.calculateSubtotal() * 0.16;
    }

    calculateTotal(): number {
        return this.calculateSubtotal() + this.calculateIVA();
    }

    submit(): void {
        if (this.maintenanceForm.invalid) {
            Object.keys(this.maintenanceForm.controls).forEach(key => {
                this.maintenanceForm.get(key)?.markAsTouched();
            });
            this.itemsArray.controls.forEach(control => {
                Object.keys((control as FormGroup).controls).forEach(key => {
                    control.get(key)?.markAsTouched();
                });
            });
            console.log('❌ Formulario inválido:', this.maintenanceForm.errors);
            return;
        }

        console.log('📤 Enviando orden de mantenimiento:', this.maintenanceForm.value);

        this.isSubmitting = true;
        const headers = new HttpHeaders().set('X-Api-Key', environment.API_KEY);

        this.http.post(
            `${environment.SERVICES_URL}/maintenance/create-order`,
            this.maintenanceForm.value,
            { headers }
        ).subscribe({
            next: (response: any) => {
                console.log('✅ Respuesta del servidor:', response);

                this.isSubmitting = false;
                if (response.success) {
                    if (this.confirmAction) {
                        this.confirmAction(response.data);
                    }
                    this.modal.dismissAll();
                } else {
                    alert('Error: ' + response.message);
                }
            },
            error: (error) => {
                console.error('❌ Error al crear orden:', error);
                this.isSubmitting = false;
                alert('Error al crear orden: ' + error.message);
            }
        });
    }

    close(): void {
        this.modal.dismissAll();
    }
}
